import { useTranslation } from "react-i18next";

import DangerIcon from "src/core/assets/images/icons/danger.svg?react";
import { EReputationStatus } from "src/core/constants/eReputation";
import { MappedStakeholder } from "src/core/constants/folder";
import {
  CustomRiskLevel,
  RISK_LEVEL_CLASSES,
  RISK_LEVEL_ICON_CLASSES,
} from "src/core/constants/risk";

type RiskColumnProps = {
  row: { original: MappedStakeholder };
  setOpenEntityDetailsModal: (open: boolean, stakeholder?: MappedStakeholder) => void;
};

export const RiskColumn = ({ row, setOpenEntityDetailsModal }: RiskColumnProps) => {
  const { t } = useTranslation();
  const renderRiskLevel = () => {
    if (row.original.riskLevel !== CustomRiskLevel.UNDEFINED) {
      return (
        <div className="flex flex-row gap-x-10">
          <div
            className={`flex items-center w-[16px] h-[16px] rounded-full ${
              RISK_LEVEL_CLASSES[row.original.riskLevel as keyof typeof RISK_LEVEL_CLASSES]
            }`}
          ></div>
          <span
            className={`font-medium ${
              RISK_LEVEL_ICON_CLASSES[
                row.original.riskLevel as keyof typeof RISK_LEVEL_ICON_CLASSES
              ]
            }`}
          >
            {t(`stakeholders.datatable.riskLevels.${row.original.riskLevel?.toLowerCase()}`)}
          </span>
        </div>
      );
    }
    return null;
  };

  if (
    row.original?.pepStatus === EReputationStatus.NO_ANALYSIS ||
    row.original?.sanctionStatus === EReputationStatus.NO_ANALYSIS
  ) {
    return (
      <div className="flex flex-col gap-y-5 px-5">
        <div className="text-orange-medium">{t("folder.entities.columns.noAnalysis")}</div>
      </div>
    );
  }

  if (
    row.original?.pepStatus === EReputationStatus.POTENTIAL ||
    row.original?.sanctionStatus === EReputationStatus.POTENTIAL
  ) {
    return (
      <div className="flex flex-col gap-y-5 px-5">
        <div
          className="stakeholders-datatable-analyse-card text-red-medium gap-x-10"
          role="button"
          onClick={() => {
            setOpenEntityDetailsModal(true, row.original);
          }}
        >
          <DangerIcon />
          {t("stakeholders.datatable.analyse.alert")}
        </div>
      </div>
    );
  }

  if (
    row.original?.pepStatus === EReputationStatus.CONFIRMED &&
    row.original?.sanctionStatus === EReputationStatus.CONFIRMED
  ) {
    return (
      <div className="flex flex-col gap-y-5 px-5">
        {renderRiskLevel()}
        <div
          className="stakeholders-datatable-analyse-card text-alert"
          role="button"
          onClick={() => {
            setOpenEntityDetailsModal(true, row.original);
          }}
        >
          {t("stakeholders.datatable.analyse.pepSanction")}
        </div>
      </div>
    );
  }

  if (
    row.original?.pepStatus === EReputationStatus.CONFIRMED &&
    row.original?.sanctionStatus !== EReputationStatus.CONFIRMED
  ) {
    return (
      <div className="flex flex-col gap-y-5 px-5">
        {renderRiskLevel()}
        <div
          className="stakeholders-datatable-analyse-card text-alert"
          role="button"
          onClick={() => {
            setOpenEntityDetailsModal(true, row.original);
          }}
        >
          {t("stakeholders.datatable.analyse.pep")}
        </div>
      </div>
    );
  }

  if (
    row.original?.pepStatus !== EReputationStatus.CONFIRMED &&
    row.original?.sanctionStatus === EReputationStatus.CONFIRMED
  ) {
    return (
      <div className="flex flex-col gap-y-5 px-5">
        {renderRiskLevel()}
        <div
          className="stakeholders-datatable-analyse-card text-alert"
          role="button"
          onClick={() => {
            setOpenEntityDetailsModal(true, row.original);
          }}
        >
          {t("stakeholders.datatable.analyse.sanction")}
        </div>
      </div>
    );
  }

  if (
    row.original?.pepStatus === EReputationStatus.FALSE_POSITIVE ||
    row.original?.sanctionStatus === EReputationStatus.FALSE_POSITIVE
  ) {
    return (
      <div className="flex flex-col gap-y-5 px-5">
        {renderRiskLevel()}
        <div
          className="stakeholders-datatable-analyse-card text-success"
          role="button"
          onClick={() => {
            setOpenEntityDetailsModal(true, row.original);
          }}
        >
          {t("stakeholders.datatable.analyse.falsePositive")}
        </div>
      </div>
    );
  }

  if (
    row.original?.pepStatus === EReputationStatus.NO_ALERT &&
    row.original?.sanctionStatus === EReputationStatus.NO_ALERT
  ) {
    return (
      <div className="flex flex-col gap-y-5 px-5">
        {renderRiskLevel()}
        <div className="text-muted text-xs font-light ml-[26px]">
          <span className="line-through">{t("stakeholders.datatable.analyse.pep")}</span>&nbsp;
          <span className="line-through">{t("stakeholders.datatable.analyse.sanction")}</span>
        </div>
      </div>
    );
  }

  return <div className="flex flex-col gap-y-5 px-5">{renderRiskLevel()}</div>;
};
